import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Smartphone, Download, Star, Zap, Shield, Wifi, WifiOff,
  BookOpen, Trophy, Brain, Heart, CheckCircle, ArrowRight,
  Apple, Play as PlayStore, Share, Plus, MoreVertical
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { motion } from 'framer-motion';

const MobileAppPage = () => {
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    setIsIOS(/iphone|ipad|ipod/.test(userAgent));
    setIsAndroid(/android/.test(userAgent));
    setIsStandalone(
      window.matchMedia('(display-mode: standalone)').matches || 
      window.navigator.standalone === true
    );

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    }
  };

  const features = [
    { icon: BookOpen, title: 'Full Curriculum', desc: 'Reading, Math, Writing & Science' },
    { icon: Brain, title: 'AI Adaptive', desc: 'Personalized learning paths' },
    { icon: Trophy, title: 'Gamified', desc: 'Badges, XP & achievements' },
    { icon: Heart, title: 'Bible Stories', desc: 'Faith-based content' },
    { icon: Shield, title: 'Safe & Secure', desc: 'Parent controls included' },
    { icon: WifiOff, title: 'Offline Mode', desc: 'Learn without internet' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-cyan-500/10" />
        <div className="max-w-6xl mx-auto px-4 py-12 md:py-20">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-center md:text-left"
            >
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Smartphone className="w-4 h-4" />
                Mobile App Available
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
                Zazu Learning App
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                Install Zazu on your device for the best learning experience. 
                Access lessons anytime, even offline!
              </p>
              
              {isStandalone ? (
                <div className="bg-emerald-100 text-emerald-700 p-4 rounded-xl mb-6">
                  <CheckCircle className="w-6 h-6 mx-auto mb-2" />
                  <p className="font-medium">App Installed!</p>
                  <p className="text-sm">You're using the Zazu app</p>
                </div>
              ) : deferredPrompt ? (
                <Button 
                  size="lg"
                  onClick={handleInstall}
                  className="bg-emerald-600 hover:bg-emerald-700 text-lg px-8"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Install App Now
                </Button>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-slate-500 font-medium">Install on your device:</p>
                  
                  {isIOS && (
                    <Card className="bg-white border-2 border-emerald-200">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center">
                            <Apple className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800">iPhone / iPad</p>
                            <p className="text-xs text-slate-500">Add to Home Screen</p>
                          </div>
                        </div>
                        <ol className="space-y-2 text-sm text-slate-600">
                          <li className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold">1</span>
                            <span>Tap <Share className="w-4 h-4 inline text-blue-500" /> Share button in Safari</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold">2</span>
                            <span>Scroll and tap <Plus className="w-4 h-4 inline" /> "Add to Home Screen"</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold">3</span>
                            <span>Tap "Add" to confirm</span>
                          </li>
                        </ol>
                      </CardContent>
                    </Card>
                  )}

                  {isAndroid && (
                    <Card className="bg-white border-2 border-emerald-200">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center">
                            <PlayStore className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800">Android</p>
                            <p className="text-xs text-slate-500">Install from browser</p>
                          </div>
                        </div>
                        <ol className="space-y-2 text-sm text-slate-600">
                          <li className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold">1</span>
                            <span>Tap <MoreVertical className="w-4 h-4 inline" /> menu in Chrome</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold">2</span>
                            <span>Tap "Install app" or "Add to Home screen"</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold">3</span>
                            <span>Tap "Install" to confirm</span>
                          </li>
                        </ol>
                      </CardContent>
                    </Card>
                  )}

                  {!isIOS && !isAndroid && (
                    <Card className="bg-white border-2 border-emerald-200">
                      <CardContent className="p-4">
                        <p className="font-semibold text-slate-800 mb-2">Desktop Browser</p>
                        <p className="text-sm text-slate-600">
                          Look for the install icon (
                          <Download className="w-4 h-4 inline" />) in your browser's address bar, 
                          or use the browser menu to "Install Zazu".
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </motion.div>

            {/* Phone Mockup */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="flex justify-center"
            >
              <div className="relative">
                <div className="w-64 h-[500px] bg-slate-900 rounded-[3rem] p-3 shadow-2xl">
                  <div className="w-full h-full bg-gradient-to-b from-emerald-500 to-cyan-600 rounded-[2.5rem] overflow-hidden relative">
                    {/* Status bar */}
                    <div className="h-6 bg-black/20 flex items-center justify-center">
                      <div className="w-20 h-4 bg-black rounded-full" />
                    </div>
                    {/* App content preview */}
                    <div className="p-4">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-2xl">
                          🦉
                        </div>
                        <div className="text-white">
                          <p className="font-bold">Hi, Alex!</p>
                          <p className="text-xs text-white/80">Ready to learn?</p>
                        </div>
                      </div>
                      <div className="bg-white/20 rounded-2xl p-3 mb-3">
                        <p className="text-white text-sm font-medium mb-2">Today's Progress</p>
                        <div className="h-2 bg-white/30 rounded-full">
                          <div className="h-full w-3/4 bg-white rounded-full" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-white/20 rounded-xl p-3 text-center">
                          <p className="text-2xl mb-1">📚</p>
                          <p className="text-white text-xs">Reading</p>
                        </div>
                        <div className="bg-white/20 rounded-xl p-3 text-center">
                          <p className="text-2xl mb-1">🔢</p>
                          <p className="text-white text-xs">Math</p>
                        </div>
                        <div className="bg-white/20 rounded-xl p-3 text-center">
                          <p className="text-2xl mb-1">✏️</p>
                          <p className="text-white text-xs">Writing</p>
                        </div>
                        <div className="bg-white/20 rounded-xl p-3 text-center">
                          <p className="text-2xl mb-1">🔬</p>
                          <p className="text-white text-xs">Science</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Decorative elements */}
                <div className="absolute -top-4 -right-4 w-20 h-20 bg-emerald-200 rounded-full blur-xl opacity-50" />
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-cyan-200 rounded-full blur-xl opacity-50" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-slate-800 mb-8">
            Everything You Need to Learn
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <feature.icon className="w-6 h-6 text-emerald-600" />
                    </div>
                    <h3 className="font-semibold text-slate-800 mb-1">{feature.title}</h3>
                    <p className="text-sm text-slate-500">{feature.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 px-4 bg-gradient-to-r from-emerald-600 to-cyan-600">
        <div className="max-w-2xl mx-auto text-center text-white">
          <h2 className="text-2xl font-bold mb-4">Start Learning Today!</h2>
          <p className="text-emerald-100 mb-6">
            Join thousands of families using Zazu to make learning fun.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-white text-emerald-600 hover:bg-emerald-50">
                Create Free Account
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 text-center text-slate-500 text-sm">
        <p>&copy; 2026 Zazu Education. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default MobileAppPage;
